import React, { useEffect, useState } from "react";
import { Form, Button, Spinner, Alert, Badge, ListGroup } from "react-bootstrap";
import * as familiaApi from "../../api/familia";
import * as perfilApi from "../../api/perfil";
import { useAuth } from "../../auth/AuthContext";
import ModalConfirmar from "../../components/ModalConfirmar";
import BuscarCrearFamiliaForm from "./BuscarCrearFamiliaForm";

export default function MiFamilia() {
  const { actualizarSesion } = useAuth();
  const [estado, setEstado] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [emailInvitar, setEmailInvitar] = useState("");
  const [mensajeInvitar, setMensajeInvitar] = useState(null);
  const [mostrarConfirmarSalir, setMostrarConfirmarSalir] = useState(false);
  const [errorSalir, setErrorSalir] = useState("");

  const cargar = async () => {
    setCargando(true);
    try {
      setEstado(await familiaApi.obtenerMiEstado());
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  // El token original solo trae los datos de familia del momento del login: tras crear/unirse/salir
  // hay que refrescarlo para que el resto de la app (ej. el menú "Familia Gastos") lo vea al toque.
  const refrescarSesionYRecargar = async () => {
    actualizarSesion(await perfilApi.refrescarToken());
    await cargar();
  };

  const handleCancelarSolicitud = async () => {
    setGuardando(true);
    try {
      await familiaApi.cancelarSolicitud();
      await cargar();
    } finally {
      setGuardando(false);
    }
  };

  const handleInvitar = async (e) => {
    e.preventDefault();
    setMensajeInvitar(null);
    setGuardando(true);
    try {
      await familiaApi.invitarMiembro(emailInvitar.trim());
      setMensajeInvitar({ tipo: "success", texto: "Invitación enviada." });
      setEmailInvitar("");
    } catch (err) {
      setMensajeInvitar({ tipo: "danger", texto: err?.response?.data?.mensaje || "No se pudo enviar la invitación." });
    } finally {
      setGuardando(false);
    }
  };

  const handleResolverSolicitud = async (token, aprobar) => {
    setGuardando(true);
    try {
      await familiaApi.resolverSolicitud(token, aprobar);
      await cargar();
    } finally {
      setGuardando(false);
    }
  };

  const handleSalir = async () => {
    setErrorSalir("");
    try {
      await familiaApi.salirDeFamilia();
      setMostrarConfirmarSalir(false);
      await refrescarSesionYRecargar();
    } catch (err) {
      setErrorSalir(err?.response?.data?.mensaje || "No se pudo salir de la familia.");
    }
  };

  if (cargando || !estado) return <Spinner animation="border" />;

  return (
    <div>
      <h4 className="mb-3">Mi Familia</h4>

      {estado.tieneFamilia ? (
        <>
          <div className="fs-card mb-3">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <div className="fw-semibold fs-5">{estado.familia.nombre}</div>
                <div className="text-muted small">Jefe: {estado.familia.nombreJefe}</div>
              </div>
              <Button size="sm" variant="outline-danger" onClick={() => { setErrorSalir(""); setMostrarConfirmarSalir(true); }}>
                <i className="bi bi-box-arrow-left me-1"></i>Salir de la familia
              </Button>
            </div>

            <ListGroup variant="flush" className="mt-3">
              {estado.miembros.map((m) => (
                <ListGroup.Item key={m.usuarioId} className="d-flex justify-content-between align-items-center px-0">
                  <span>{m.nombres} <span className="text-muted small">· {m.email}</span></span>
                  <Badge bg={m.rolFamilia === "Jefe" ? "primary" : "secondary"}>{m.rolFamilia}</Badge>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>

          {estado.esJefe && (
            <>
              <div className="fs-card mb-3">
                <div className="fw-semibold mb-2">Invitar miembro</div>
                {mensajeInvitar && <Alert variant={mensajeInvitar.tipo} className="py-2">{mensajeInvitar.texto}</Alert>}
                <Form onSubmit={handleInvitar}>
                  <div className="d-flex gap-2 flex-wrap">
                    <Form.Control
                      type="email"
                      style={{ maxWidth: 320 }}
                      placeholder="Correo ya registrado en FinanzasSys"
                      value={emailInvitar}
                      onChange={(e) => setEmailInvitar(e.target.value)}
                      required
                    />
                    <Button type="submit" variant="primary" disabled={guardando}>
                      {guardando ? <Spinner size="sm" animation="border" /> : "Invitar"}
                    </Button>
                  </div>
                </Form>
              </div>

              <div className="fs-card mb-3">
                <div className="fw-semibold mb-2">Solicitudes pendientes</div>
                {!estado.solicitudesPendientes.length && <p className="text-muted small mb-0">No hay solicitudes pendientes.</p>}
                <ListGroup variant="flush">
                  {estado.solicitudesPendientes.map((s) => (
                    <ListGroup.Item key={s.solicitudId} className="d-flex justify-content-between align-items-center px-0">
                      <span>{s.nombres} <span className="text-muted small">· {s.email}</span></span>
                      <div className="d-flex gap-1">
                        <Button size="sm" variant="outline-success" disabled={guardando} onClick={() => handleResolverSolicitud(s.token, true)}>
                          <i className="bi bi-check-lg"></i>
                        </Button>
                        <Button size="sm" variant="outline-danger" disabled={guardando} onClick={() => handleResolverSolicitud(s.token, false)}>
                          <i className="bi bi-x-lg"></i>
                        </Button>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </div>
            </>
          )}
        </>
      ) : estado.solicitudPropia ? (
        <div className="fs-card" style={{ maxWidth: 520 }}>
          <i className="bi bi-hourglass-split me-2"></i>
          Esperando respuesta de <strong>{estado.solicitudPropia.nombreJefe}</strong> para unirte a{" "}
          <strong>{estado.solicitudPropia.nombreFamilia}</strong>.
          <div className="mt-3">
            <Button size="sm" variant="outline-secondary" onClick={handleCancelarSolicitud} disabled={guardando}>
              {guardando ? <Spinner size="sm" animation="border" /> : "Cancelar solicitud"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="fs-card" style={{ maxWidth: 520 }}>
          <BuscarCrearFamiliaForm onResuelto={refrescarSesionYRecargar} />
        </div>
      )}

      <ModalConfirmar
        show={mostrarConfirmarSalir}
        titulo="Salir de la familia"
        mensaje='Al salir vas a perder todo lo que guardaste en Familia Gastos (tu aporte a cada gasto, tu sueldo declarado, y tus Ajustes). Si vuelves a entrar -- a esta o a otra familia -- empiezas de cero. ¿Seguro que quieres salir?'
        textoConfirmar="Salir"
        error={errorSalir}
        onConfirmar={handleSalir}
        onCancelar={() => setMostrarConfirmarSalir(false)}
      />
    </div>
  );
}
